import React, {useEffect, useState} from 'react';

import Container from 'components/container';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import {fetch} from "../../services/fetch";
import {AxiosError, AxiosResponse} from "axios";
import {useNavigate, useParams} from "react-router-dom";
import {Col, Row, Form, Button, Modal} from "react-bootstrap";
import Table from "react-bootstrap/Table";
import {dashboard} from "../../routes/uris";

const parker = 'parker';
const request = 'request';
const account = 'account';

type UdfItem = {
    flash_table: typeof parker | typeof request;
    flash_column: string;
    paris_udf_type: typeof parker | typeof account;
    paris_udf_id: string;
}

type ParisLocation = {
    id: number,
    facility_id: number,
    location_name: string
}

type ParisUdf = {
    id: number,
    paris_file_id: number,
    udf_id: number,
    udf_type: string,
    title: string
}

type SubmissionData = {
    listing_id: number,
    facility_id: number,
    flash_external_override: boolean,
    parcs_inventory: boolean,
    fake_email_parkers: boolean
    udf_mapping?: UdfItem[]
};

const flashArColumns = {
    parker: [
        'licensePlate1',
        'make1',
        'model1',
        'state1',
        'number',
        'name',
        'licensePlate2',
        'make2',
        'model2',
        'state2',
        'licensePlate3',
        'make3',
        'model3',
        'state3',
        'PARCS_CardStatus',
        'PARCS_AccessLevel',
        'phone',
        'start',
        'end',
        'tag1',
        'tag2',
        'tag3',
        'PARCS_AccountIDOverride',
        'PARCS_ParkerIDOverride',
        'PARCS_Vehicle1IDOverride',
        'PARCS_Vehicle2IDOverride',
        'PARCS_Vehicle3IDOverride',
        'barcode',
        'employeeID',
        'reportingGroup',
        'keyBarcode1',
        'vehicleBarcode1',
        'keyHook1',
        'keyBarcode2',
        'vehicleBarcode2',
        'keyHook2',
        'keyBarcode3',
        'vehicleBarcode3',
        'keyHook3',
        'PARCS_LateFeeOnKiosk',
        'pin',
        'PassBack',
        'department',
    ],
    request: [
        'dateRequested',
        'active',
        'end',
        'spaceNumber',
        'start',
        'status',
        'companyName',
        'parkitLead',
        'billing',
        'onboard',
        'completedSignup',
        'cardToken',
        'stripeCustomerID',
        'notes',
        'messageToOp',
        'achToken',
        'ACHAuthFormSigned',
        'ACHAuthFormSignedDate',
        'PARCS_AccessLevel',
        'PARCS_CardStatus',
        'PARCS_AutoDeactivate',
        'PARCS_PoolName',
        'billingType',
        'accountNumber',
        'PARCS_LateFeeOnKiosk',
        'PARCS_AllowPassBack',
        'PARCS_ExternalID',
        'PARCS_Prefix',
        'poNumber',
        'lateFeeBilling',
        'requireVehicles',
        'agreementSignatureDate',
        'agreementSignatureDocument',
    ],
};

const Convert: React.FC = () => {
    const [flashListingId, setFlashListingId] = useState<number>();
    const [parisFacilityId, setParisFacilityId] = useState<number>();
    const [useFlashExternalId, setUseFlashExternalId] = useState<boolean>(false);
    const [useParcsInventory, setUseParcsInventory] = useState<boolean>(false);
    const [useFakeEmails, setUseFakeEmails] = useState<boolean>(true);
    const [parisLocations, setParisLocations] = useState<ParisLocation[]>([]);
    const [parisUdfs, setParisUdfs] = useState<ParisUdf[]>([]);
    const [udfItems, setUdfItems] = useState<UdfItem[]>([]);
    const [formSubmitted, setFormSubmitted] = useState<boolean>(false);
    const [returnToConversions, setReturnToConversions] = useState<boolean>(false);
    const [facilityIdChange, setFacilityIdChange] = useState<boolean>(false);

    const navigate = useNavigate();

    const parisFileId = useParams().id;

    useEffect(() => {
        fetch(`/conversion/${parisFileId}`, 'GET', {}, true)
            .then((result: AxiosResponse) => {
                if (result.data.data.length) {
                    const { paris_facility_id, paris_configurations } = result.data.data.pop();

                    if (paris_configurations.length) {
                        setUdfItems(paris_configurations);
                        setParisFacilityId(paris_facility_id);
                    }
                }
            });
    }, [parisFileId]);

    useEffect(() => {
        fetch(`/paris/location-setup/${parisFileId}` , 'GET', {}, true)
            .then((result: AxiosResponse) => {
                setParisLocations(result.data.data);
            });
    }, []);

    useEffect(() => {
        if (!parisFacilityId) {
            return setParisUdfs([]);
        }

        fetch(`/paris/udf/${parisFileId}/${parisFacilityId}` , 'GET', {}, true)
            .then((result: AxiosResponse) => {
                setParisUdfs(result.data.data);
            });
    }, [parisFacilityId]);

    const setParisFacilityIdAndClearUdfTitle = (facilityId: number) => {
        setParisFacilityId(facilityId);

        if (udfItems.length > 0) {
            setFacilityIdChange(true);

            const cleanUdfTitleList: UdfItem[] = [];

            for (const udfItem of udfItems) {
                cleanUdfTitleList.push({
                    ...udfItem,
                    paris_udf_id: undefined
                });
            }

            setUdfItems(cleanUdfTitleList);
        }
    };

    const addBlankUdfListRow = () => {
        const blankUdfObject: UdfItem = {
            flash_column: undefined,
            flash_table: parker,
            paris_udf_id: undefined,
            paris_udf_type: parker,
        };

        const udfListWithAddedItem: UdfItem[] = [...udfItems];
        udfListWithAddedItem.push(blankUdfObject);

        setUdfItems(udfListWithAddedItem);
    };

    const updateUdfListItem = (value: string, udfListIndex: number, key: string) => {
        const updatedUdfList: UdfItem[] = [...udfItems];
        updatedUdfList[udfListIndex][key] = value !== '' ? value : undefined;
        setUdfItems(updatedUdfList);
    };

    const deleteUdfListItem = (udfListIndex: number) => {
        const udfListWithoutItem: UdfItem[] = [...udfItems];
        udfListWithoutItem.splice(udfListIndex, 1);

        setUdfItems(udfListWithoutItem);
    };

    const createConversion = (ev: React.MouseEvent<HTMLFormElement>) => {
        ev.preventDefault();
        ev.stopPropagation();

        if (ev.currentTarget.reportValidity()) {
            setFormSubmitted(true);

            const data: SubmissionData = {
                listing_id: flashListingId,
                facility_id: parisFacilityId,
                flash_external_override: useFlashExternalId,
                parcs_inventory: useParcsInventory,
                fake_email_parkers: useFakeEmails
            };

            if (udfItems.length > 0) {
                data.udf_mapping = udfItems;
            }

            fetch(`/conversion/${parisFileId}` , 'POST', data, true)
                .then(() => {
                    setReturnToConversions(true);
                }).catch((error: AxiosError) => {
                    //@todo rollbar
                    console.log(error.response.data);
                    setFormSubmitted(false);
                });
        }
    };

    const renderFLASHARColumnOptions = (currentUdfItem: UdfItem, currentIndex: number) => {
        const usedFLASHColumns = udfItems
            .filter((udfItem: UdfItem, index: number) => currentIndex !== index )
            .map((udfItem: UdfItem) => udfItem.flash_column);

        return flashArColumns[currentUdfItem.flash_table]
            .filter((column: string) => !usedFLASHColumns.includes(column))
            .map((column: string) => <option key={column} value={column}>{column}</option>);
    };

    return (
        <Container>
            <h2 className="mt-3 mb-0">Upload an MS SQL Database File</h2>
            <p className="mb-4">Please read the <a href="https://flash-team.atlassian.net/l/c/zpCP3qJT">how to</a> instructions on using the conversion tool.<br/>To upload an access file, please first convert it to MS SQL format </p>
            <Form noValidate={true} onSubmit={createConversion}>
                <Row>
                    <Col>
                        <Form.Group>
                            <Form.Label htmlFor="listing-id">FLASH A/R Listing ID to import to</Form.Label>
                            <Form.Control type="number" required value={flashListingId ?? ''} onChange={(ev: React.ChangeEvent<HTMLInputElement>) => setFlashListingId(parseInt(ev.target.value) || undefined)}/>
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group className="mb-3">
                            <Form.Label htmlFor="paris-facility-id">PARIS Location to convert</Form.Label>
                            <Form.Select required value={parisFacilityId ?? ''} onChange={(ev: React.ChangeEvent<HTMLSelectElement>) => setParisFacilityIdAndClearUdfTitle(parseInt(ev.target.selectedOptions[0].value) || undefined)}>
                                <option value="">Select a location</option>
                                {parisLocations.map((location: ParisLocation) => <option key={location.id} value={location.facility_id}>{location.location_name}</option>)}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Form.Group>
                            <Form.Label>Use FLASH External ID Override?</Form.Label>
                            <Form.Check
                                required
                                checked={useFlashExternalId === true}
                                type="radio"
                                id="flashExternalIdYes"
                                name="flashExternalId"
                                label="Yes"
                                onChange={() => setUseFlashExternalId(true)}
                            />
                            <Form.Check
                                required
                                checked={useFlashExternalId === false}
                                type="radio"
                                id="flashExternalIdNo"
                                name="flashExternalId"
                                label="No"
                                onChange={() => setUseFlashExternalId(false)}
                            />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group>
                            <Form.Label>Use PARCS Inventory?</Form.Label>
                            <Form.Check
                                required
                                checked={useParcsInventory === true}
                                type="radio"
                                id="parcsInventoryYes"
                                name="parcsInventory"
                                label="Yes"
                                onChange={() => setUseParcsInventory(true)}
                            />
                            <Form.Check
                                required
                                checked={useParcsInventory === false}
                                type="radio"
                                id="parcsInventoryNo"
                                name="parcsInventory"
                                label="No"
                                onChange={() => setUseParcsInventory(false)}
                            />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group>
                            <Form.Label className="mb-0">Use Fake emails for parkers?</Form.Label><br/>
                            <Form.Text>Do this for sandbox/test locations</Form.Text>
                            <Form.Check
                                required
                                checked={useFakeEmails === true}
                                type="radio"
                                id="fakeEmailsYes"
                                name="fakeEmails"
                                label="Yes"
                                onChange={() => setUseFakeEmails(true)}
                            />
                            <Form.Check
                                required
                                checked={useFakeEmails === false}
                                type="radio"
                                id="fakeEmailsNo"
                                name="fakeEmails"
                                label="No"
                                onChange={() => setUseFakeEmails(false)}
                            />
                        </Form.Group>
                    </Col>
                </Row>

                <Row><h3 className="mt-3">UDF Mapping</h3></Row>
                <Row>
                    <Col>
                        <Table>
                            <thead>
                                <tr>
                                    <th></th>
                                    <th>FLASH A/R Table</th>
                                    <th>FLASH A/R Column</th>
                                    <th>PARIS UDF Type</th>
                                    <th>PARIS UDF Title</th>
                                </tr>
                            </thead>
                            <tbody>
                                {udfItems.map((udfItem: UdfItem, index: number) => {
                                    return (
                                        <tr key={`udf-item-${index}`}>
                                            <td align="center" valign="middle">
                                                <FontAwesomeIcon icon={faTrash} className="text-danger" onClick={() => deleteUdfListItem(index)} />
                                            </td>
                                            <td>
                                                <Form.Select
                                                    required
                                                    value={udfItem.flash_table}
                                                    onChange={(ev: React.ChangeEvent<HTMLSelectElement>) => updateUdfListItem(ev.target.selectedOptions[0].value, index, 'flash_table')}
                                                >
                                                    <option value={parker}>Parker</option>
                                                    <option value={request}>Request</option>
                                                </Form.Select>
                                            </td>
                                            <td>
                                                <Form.Select
                                                    required
                                                    value={udfItem.flash_column}
                                                    onChange={(ev: React.ChangeEvent<HTMLSelectElement>) => updateUdfListItem(ev.target.selectedOptions[0].value, index, 'flash_column')}
                                                >
                                                    <option value="">Select an option</option>
                                                    {renderFLASHARColumnOptions(udfItem, index)}
                                                </Form.Select>
                                            </td>
                                            <td>
                                                <Form.Select
                                                    required
                                                    value={udfItem.paris_udf_type}
                                                    onChange={(ev: React.ChangeEvent<HTMLSelectElement>) => updateUdfListItem(ev.target.selectedOptions[0].value, index, 'paris_udf_type')}
                                                >
                                                    <option value={parker}>Parker</option>
                                                    <option value={account}>Account</option>
                                                </Form.Select>
                                            </td>
                                            <td>
                                                <Form.Select
                                                    required
                                                    value={udfItem.paris_udf_id ?? ''}
                                                    onChange={(ev: React.ChangeEvent<HTMLSelectElement>) => updateUdfListItem(ev.target.selectedOptions[0].value, index, 'paris_udf_id')}
                                                >
                                                    <option value="">Select an option</option>
                                                    {parisUdfs.filter((parisUdf: ParisUdf) => parisUdf.udf_type === udfItem.paris_udf_type).map((parisUdf: ParisUdf) => <option key={parisUdf.id} value={parisUdf.id}>{parisUdf.title}</option>)}
                                                </Form.Select>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Button variant="success" onClick={addBlankUdfListRow}>Add Row</Button>
                    </Col>
                </Row>
                <Row>
                    <Col>
                        <Button type="submit" className="mt-4" variant="primary" disabled={formSubmitted}>Run Conversion</Button>
                    </Col>
                </Row>
            </Form>

            <Modal
                show={returnToConversions}
                backdrop="static"
                keyboard={false}
            >
                <Modal.Body>
                    The conversion has been started successfully. You&apos;ll now be returned to the conversions list.
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={() => {navigate(dashboard);}}>Ok</Button>
                </Modal.Footer>
            </Modal>

            <Modal
                show={facilityIdChange}
                backdrop="static"
                keyboard={false}
            >
                <Modal.Body>
                    Please review your UDF mapping. You changed the Paris Location, so we cleared the <strong>PARIS UDF Title</strong> column.
                </Modal.Body>
                <Modal.Footer>
                    <Button
                        variant="primary"
                        onClick={() => {
                            setFacilityIdChange(false);
                        }}
                    >
                        Ok
                    </Button>
                </Modal.Footer>
            </Modal>
        </Container>
    );
};

export default Convert;
